import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

export type SubscriptionStatus = "active" | "inactive" | "trialing" | "past_due" | "unpaid" | "canceled" | "stripe_not_configured" | "missing_payment_info";

export interface SubscriptionInfo {
  customerId?: string;
  subscriptionId?: string;
  status?: SubscriptionStatus;
  plan?: string;
  currentPeriodEnd?: string;
  currentPeriodStart?: string;
  trialStart?: string;
  trialEnd?: string;
  subscriptionCreated?: string;
  businessCreatedAt?: string;
  isActive?: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  phoneNumber?: string;
  isAdmin: boolean;
  business: {
    id: string;
    name: string;
    website?: string;
  };
}

export interface UpdateUserProfileData {
  email?: string;
  phoneNumber?: string;
  businessName?: string;
  website?: string;
}

export function useBillingData() {
  return useQuery<SubscriptionInfo, Error>({
    queryKey: ['billing'],
    queryFn: async () => {
      const { data } = await api.get<SubscriptionInfo>('/billing/subscription-status');
      return data;
    },
  });
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ url: string }>('/billing/create-portal-session');
      return data;
    },
  });
}

export function useSubscriptionStatus(isAuthenticated: boolean = true) {
  return useQuery({
    queryKey: ['subscription-status'],
    queryFn: async () => {
      const { data } = await api.get('/billing/subscription-status');
      return data;
    },
    enabled: isAuthenticated,
  });
}

export function useUserProfile() {
  return useQuery<UserProfile, Error>({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const { data } = await api.get<UserProfile>('/profile');
      return data;
    },
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: UpdateUserProfileData) => {
      const { data: responseData } = await api.put<UserProfile>('/profile', data);
      return responseData;
    },
    onSuccess: (data) => {
      // Update the user profile cache
      queryClient.setQueryData(['user-profile'], data);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });
} 